import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";
import {
  useGetSubscriptionStatusQuery,
  useCancelSubscriptionMutation,
} from "../../../api/paymentsApi";
import UpgradeModal from "../upgrade-modal/UpgradeModal";
import styles from "./UpgradeCard.module.scss";

const cx = classNames.bind(styles);

const UpgradeCard: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { data: subscription } = useGetSubscriptionStatusQuery();
  const [cancelSubscription, { isLoading: isCanceling }] = useCancelSubscriptionMutation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isPremium = subscription?.tier === "PREMIUM" && subscription?.status === "active";
  const isCanceled = subscription?.cancel_at_period_end;

  const handleCancel = async () => {
    if (!window.confirm(t("subscription.cancelConfirm", "Are you sure you want to cancel your subscription? You will retain access until the end of the billing period."))) {
      return;
    }
    try {
      await cancelSubscription().unwrap();
    } catch {
      // Error handled by RTK Query baseQuery popup
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString(i18n.language, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <div className={cx("upgradeCard")}>
        <div className={cx("cardHeader")}>
          <div className={cx("crownIcon")}>
            <i className="fas fa-crown"></i>
          </div>
          <h3 className={cx("cardTitle")}>
            {t("subscription.plan", "Subscription")}
          </h3>
        </div>

        {isPremium ? (
          <>
            <div className={cx("premiumInfo")}>
              <span className={cx("currentPlan", "premium")}>
                <i className="fas fa-crown"></i>
                {t("subscription.premiumPlan", "Premium")}
              </span>
              {subscription?.current_period_end && (
                <p className={cx("renewalDate")}>
                  {isCanceled
                    ? t("subscription.expiresOn", "Expires on")
                    : t("subscription.renewsOn", "Renews on")}{" "}
                  <strong>{formatDate(subscription.current_period_end)}</strong>
                </p>
              )}
              {isCanceled && (
                <p className={cx("cancelNotice")}>
                  {t("subscription.canceledNotice", "Your subscription will not renew")}
                </p>
              )}
            </div>
            {!isCanceled && (
              <button
                className={cx("cancelButton")}
                onClick={handleCancel}
                disabled={isCanceling}
              >
                {isCanceling
                  ? t("common.loading", "Loading...")
                  : t("subscription.cancelPlan", "Cancel subscription")}
              </button>
            )}
          </>
        ) : (
          <>
            <div className={cx("freeSection")}>
              <span className={cx("currentPlan", "free")}>
                {t("subscription.freePlan", "Free Plan")}
              </span>
              <ul className={cx("featureHighlights")}>
                <li className={cx("featureHighlight")}>
                  <i className="fas fa-check"></i>
                  {t("subscription.featurePreview0", "Unlock premium exercises")}
                </li>
                <li className={cx("featureHighlight")}>
                  <i className="fas fa-check"></i>
                  {t("subscription.featurePreview1", "Unlimited attempts & grammar checks")}
                </li>
                <li className={cx("featureHighlight")}>
                  <i className="fas fa-check"></i>
                  {t("subscription.featurePreview2", "Export vocabulary to Anki/CSV")}
                </li>
              </ul>
            </div>
            <button
              className={cx("upgradeButton")}
              onClick={() => setIsModalOpen(true)}
            >
              <i className="fas fa-crown"></i>
              {t("subscription.upgradeToPremium", "Upgrade to Premium")}
            </button>
          </>
        )}
      </div>

      <UpgradeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default UpgradeCard;
